import { Formik } from 'formik';
import { useCallback } from 'react';
import { object } from 'yup';

import type { DimensionType, ToleranceType, Dimension } from '@frontend/api';
import { TOLERANCE_TYPES, DIMENSION_TYPES } from '@frontend/api';
import {
  Flex,
  FormControl,
  FormLabel,
  FormErrorMessage,
  GDTSymbolText,
} from '@frontend/components';

import { SubFormProps } from '../types';
import { FormPreviewSection } from './shared/FormPreviewSection';
import { MultiplierInput } from './shared/MultiplierInput';
import { NumberValueField } from './shared/NumberInputValue';
import { SubFormWrapper } from './shared/SubFormWrapper';
import { ToleranceInputGroup } from './shared/ToleranceInputGroup';
import { DimensionTypeSelect } from './shared/TypedSelects';
import {
  createDimensionTypeValidation,
  createRequiredStringNumberField,
  createStringToleranceValidationGroup,
  createMultiplierValidationGroup,
  parsePrecisionValue,
} from './shared/validation';
import {
  buildPrecisionMeasurementObject,
  stringToleranceFormValuesToConfig,
  extractMultiplier,
} from './shared/valueBuilders';

type DimensionFormValues = {
  dimType: DimensionType;
  value: string;
  hasMult: boolean;
  mult?: number;
  hasTolerance: boolean;
  toleranceType: ToleranceType;
  toleranceUpper?: string;
  toleranceLower?: string;
  toleranceSymmetric?: string;
};

/**
 * Converts DimensionFormValues into a concise string summary using appropriate symbols
 * @param values - The dimension form values to convert
 * @returns A formatted string representation of the dimension
 */
const formatDimensionSummary = (values: DimensionFormValues): string => {
  let summary = '';

  // Add multiplier first if present
  if (values.hasMult && values.mult !== undefined) {
    summary += `${values.mult}×`;
  }

  // Add dimension type symbol prefix
  switch (values.dimType) {
    case DIMENSION_TYPES.DIAMETER:
      summary += '⌀';
      break;
    case DIMENSION_TYPES.RADIUS:
      summary += 'R';
      break;
    case DIMENSION_TYPES.LINEAR:
    default:
      // No prefix for linear dimensions
      break;
  }

  // Add the main value preserving user input format
  summary += values.value || '0';

  // Add tolerance if present
  if (values.hasTolerance) {
    switch (values.toleranceType) {
      case TOLERANCE_TYPES.SYMMETRIC:
        if (values.toleranceSymmetric) {
          summary += ` ±${values.toleranceSymmetric}`;
        }
        break;
      case TOLERANCE_TYPES.ASYMMETRIC:
        if (values.toleranceUpper && values.toleranceLower) {
          summary += ` +${values.toleranceUpper}/-${values.toleranceLower}`;
        }
        break;
      case TOLERANCE_TYPES.LIMIT:
        if (values.toleranceUpper && values.toleranceLower) {
          summary += ` [${values.toleranceLower}, ${values.toleranceUpper}]`;
        }
        break;
    }
  }

  return summary;
};

const initialValues: DimensionFormValues = {
  dimType: DIMENSION_TYPES.LINEAR,
  value: '0',
  hasMult: false,
  mult: undefined,
  hasTolerance: false,
  toleranceType: TOLERANCE_TYPES.SYMMETRIC,
  toleranceUpper: undefined,
  toleranceLower: undefined,
  toleranceSymmetric: undefined,
};

const validationSchema = object().shape({
  dimType: createDimensionTypeValidation(),
  value: createRequiredStringNumberField('Value'),
  ...createMultiplierValidationGroup(),
  ...createStringToleranceValidationGroup(),
});

export const DimensionForm = ({
  onSubmit,
  onCancel,
  renderSelectedType,
}: SubFormProps) => {
  const handleSubmit = useCallback(
    (values: DimensionFormValues) => {
      // Parse the main value to get numeric value for tolerance calculations
      const parsedValue = parsePrecisionValue(values.value);
      if (!parsedValue) {
        // This should be caught by validation, but just in case
        return;
      }

      // Build measurement with tolerance using string-based values
      const toleranceConfig = stringToleranceFormValuesToConfig(
        {
          hasTolerance: values.hasTolerance,
          toleranceType: values.toleranceType,
          toleranceSymmetric: values.toleranceSymmetric,
          toleranceUpper: values.toleranceUpper,
          toleranceLower: values.toleranceLower,
        },
        parsedValue.numericValue, // Use numeric value for tolerance calculations
      );

      const meas = buildPrecisionMeasurementObject(
        values.value,
        toleranceConfig,
      );
      if (!meas) {
        // This should be caught by validation, but just in case
        return;
      }

      // Build the final Dimension object
      const dimension: Dimension = {
        calloutType: 'dim',
        dimType: values.dimType,
        mult: extractMultiplier({ hasMult: values.hasMult, mult: values.mult }),
        meas,
      };

      onSubmit(dimension, formatDimensionSummary(values));
    },
    [onSubmit],
  );

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ values, setFieldValue, errors, touched, isValid, isSubmitting }) => (
        <SubFormWrapper
          isValid={isValid}
          isSubmitting={isSubmitting}
          onCancel={onCancel}
          renderSelectedType={renderSelectedType}
          submitLabel="Add Dimension"
        >
          {/* Preview Section */}
          <FormPreviewSection>
            <GDTSymbolText size="md">
              {formatDimensionSummary(values)}
            </GDTSymbolText>
          </FormPreviewSection>

          {/* Form Fields */}
          <Flex direction="column" gap={1} px={1} pb={1}>
            {/* Dimension Type Select */}
            <DimensionTypeSelect
              name="dimType"
              label="Dimension Type"
              value={values.dimType}
              onChange={(value) => setFieldValue('dimType', value)}
              error={errors.dimType}
              touched={touched.dimType}
            />

            {/* Dimension Value */}
            <FormControl>
              <FormLabel>Value</FormLabel>
              <NumberValueField
                name="value"
                value={values.value}
                onChange={(value) => setFieldValue('value', value)}
                size="sm"
              />
              {errors.value && touched.value && (
                <FormErrorMessage>{errors.value}</FormErrorMessage>
              )}
            </FormControl>

            {/* Tolerance */}
            <ToleranceInputGroup
              hasToleranceFieldName="hasTolerance"
              hasTolerance={values.hasTolerance}
              onHasToleranceChange={(checked) =>
                setFieldValue('hasTolerance', checked)
              }
              toleranceTypeFieldName="toleranceType"
              toleranceType={values.toleranceType}
              onToleranceTypeChange={(type) =>
                setFieldValue('toleranceType', type)
              }
              symmetricFieldName="toleranceSymmetric"
              symmetricValue={values.toleranceSymmetric}
              onSymmetricChange={(value) =>
                setFieldValue('toleranceSymmetric', value)
              }
              upperFieldName="toleranceUpper"
              upperValue={values.toleranceUpper}
              onUpperChange={(value) => setFieldValue('toleranceUpper', value)}
              lowerFieldName="toleranceLower"
              lowerValue={values.toleranceLower}
              onLowerChange={(value) => setFieldValue('toleranceLower', value)}
              errors={errors}
              touched={touched}
            />

            {/* Multiplier */}
            <MultiplierInput
              hasMultFieldName="hasMult"
              hasMult={values.hasMult}
              onHasMultChange={(checked) => setFieldValue('hasMult', checked)}
              multFieldName="mult"
              multValue={values.mult}
              onMultChange={(value) => setFieldValue('mult', value)}
              errors={errors}
              touched={touched}
            />
          </Flex>
        </SubFormWrapper>
      )}
    </Formik>
  );
};
